/**
 * Modsy Navigation Bar.
 *
 * Remains fixed to top of screen.
 * Includes Modsy Logo in TLHC of navbar.
 * Supports both Desktop and Mobile (small-screen) versions.
 * For small screens collapses to a burger icon in RHC of
 * navbar and switches to a Detail menu that slides in from
 * the left when tapping on the burger icon.
 *
 * Users of navigation bar pass in NavigationItems which
 * allows for dynamic customization. Items can be passed
 * for LHS next to logo and RHS edge of screen. For mobile,
 * burger menu items can be passed as well as items that
 * will be located next to burger icon.
 *
 * By design, soaks up empty space under itself
 * so that outside screen computations do not need
 * to care about the height of the navigation bar.
 */
import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';

import { museumGrey } from 'web-ui-kit/colors';
import { Box, Flex, Image } from 'modsy-ui-kit';
import {
  MQ_FOR_MOBILE_BELOW,
  DisplayToggle,
  QUERY_FOR_MOBILE_UP,
  QUERY_FOR_TABLET_LANDSCAPE_UP
} from 'web-ui-kit/media';

import analytics from 'services/analytics';
import { navigateToHomePage } from 'services/routing';

import { Banner } from 'screens/Project/components/RetailPartnerBanner';

import modsyLogo from './modsy-logo-identity@2x.png';
import modsyHeart from 'static/images/studio/icons/modsy-heart.png';
import MenuNavigation from './MenuNavigation';
import NavItemsFull, { NavItems } from './NavItemsFull';

import { register } from 'shared/components/Registry';
import { compose } from 'redux';
import { getIsPhone } from 'shared/selectors/device';

import { NAV_BAR_ID, NAV_BAR_CONTENT_ID, PARTNER_BANNER_HEIGHT } from './constants';

export const NAV_BAR_HEIGHT_MOBILE = 50;
export const NAV_BAR_HEIGHT_DESKTOP = 64;

/**
 * Modsy Navigation Bar.
 */
class NavigationBar extends React.Component {
  render () {
    const {
      isPhone,
      navbarHeightDesktop,
      navbarHeightMobile,
      partnerBanner,
      studioBannerHeight,
      positionFixed
    } = this.props;

    // The total height of the navigation bar including
    // optional retail partner banner.
    const totalHeight = (isPhone ? navbarHeightMobile : navbarHeightDesktop) +
      (partnerBanner ? PARTNER_BANNER_HEIGHT : 0);

    return (
      <Container id={NAV_BAR_ID} totalHeight={totalHeight} hidden={this.props.hidden}>
        <NavbarContainer height={totalHeight} top={studioBannerHeight} positionFixed={positionFixed}>
          {partnerBanner && this.renderPartnerBanner()}

          <NavbarContent id={NAV_BAR_CONTENT_ID} navbarHeight={totalHeight}>
            {this.renderLogo()}
            <DisplayToggle query={QUERY_FOR_TABLET_LANDSCAPE_UP}>
              {this.renderNavigationSmallScreen()}
              {this.renderNavigation()}
            </DisplayToggle>
          </NavbarContent>
          <NavBarBorder />
        </NavbarContainer>
      </Container>
    );
  }

  renderPartnerBanner () {
    const { partnerBanner } = this.props;

    return (
      <PartnerBanner>
        <Banner bgImage={[`url(${partnerBanner})`]} />
      </PartnerBanner>
    );
  }

  renderLogo () {
    const { logoClick, showLogo = true } = this.props;

    return (
      <LogoContainer showLogo={showLogo} onClick={logoClick}>
        <DisplayToggle query={QUERY_FOR_MOBILE_UP}>
          <Image src={modsyHeart} alt='modsy-logo' />
          <Image src={modsyLogo} alt='modsy-logo' />
        </DisplayToggle>
      </LogoContainer>
    );
  }

  renderNavigationSmallScreen () {
    const {
      isPhone,
      navbarHeightDesktop,
      navbarHeightMobile,
      navItemsMenu,
      navItemsLeftOfMenu,
      navItemsLhs,
      onToggle
    } = this.props;

    const navbarHeight = isPhone ? navbarHeightMobile : navbarHeightDesktop;

    return (
      <React.Fragment>
        {navItemsLhs &&
          <Flex justify='flex-start' row width={1}>
            <NavItems>{navItemsLhs}</NavItems>
          </Flex>
        }
        {navItemsLeftOfMenu &&
          <NavItemsLeftOfMenuContainer row>
            {navItemsLeftOfMenu}
          </NavItemsLeftOfMenuContainer>
        }
        {navItemsMenu &&
          <MenuNavigation
            onToggle={onToggle}
            navItemsMenu={navItemsMenu}
            navbarHeight={navbarHeight}
          />
        }
      </React.Fragment>
    );
  }

  renderNavigation () {
    const {
      navItemsLhs,
      navItemsRhs
    } = this.props;

    return (
      <NavItemsFull
        navItemsLhs={navItemsLhs}
        navItemsRhs={navItemsRhs}
      />
    );
  }
}

const mapStateToProps = state => ({
  isPhone: getIsPhone(state)
});

const mapDispatchToProps = dispatch => ({
  logoClick () {
    dispatch(analytics.headerClick({ linkName: 'Modsy Logo' }));
    navigateToHomePage();
  }
});

const withRegistry = register(NAV_BAR_ID);
const withContentRegistry = register(NAV_BAR_CONTENT_ID);

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect, withRegistry, withContentRegistry)(NavigationBar);

/**
 * Container.
 * Soaks up empty space behind navbar.
 */
const Container = styled(Box)`
  display: inline-block;
  flex-shrink: 0;
  height: ${props => props.totalHeight}px;
  vertical-align: middle;
  width: 100%;
  z-index: 11;
  ${props => props.hidden && `display: none;`}
`;

/**
 * Navbar container.
 * Fixed to top of page on top of any content that my scroll.
 */
const NavbarContainer = styled(Flex)`
  align-items: center;
  background-color: white;
  flex-direction: column;
  height: ${props => props.height}px;
  justify-content: flex-start;
  /* removed the "position: fixed" to temporarily fix the overlap of the merch banner */
  top: ${props => props.top}px;
  left: 0;
  width: 100%;
  z-index: 14;
  position: ${props => props.positionFixed ? 'fixed' : 'static'};
`;

/**
 * Viewable navbar content area.
 */
const NavbarContent = styled(Flex)`
  align-items: center;
  background: #fff;
  flex-direction: row;
  height: ${props => props.navbarHeight}px;
  padding 0 24px;
  ${MQ_FOR_MOBILE_BELOW} {
    padding: 0 9px;
  }
  width: 100%;
`;

const NavBarBorder = styled.hr`
  border: 0.4px solid ${museumGrey};
  bottom: 0;
  margin: 0;
  position: absolute;
  width: 100%;
`;

/**
 * Logo fixed to left hand side.
 */
const LogoContainer = styled(Box)`
  cursor: pointer;
  flex-shrink: 0;
  margin-right: 32px;
  width: 145px;
  ${MQ_FOR_MOBILE_BELOW} {
    margin-right: 24px;
    width: 20px;
  }
  display: ${props => props.showLogo ? 'initial' : 'none'};
`;

/**
 * Partner Banner.
 * TODO: need to actually display the banner.
 */
const PartnerBanner = styled(Box)`
  height: ${PARTNER_BANNER_HEIGHT}px;
  width: 100%;
  z-index: 11;
  background-color: #494949;
`;

const NavItemsLeftOfMenuContainer = styled(Flex)`
  align: center;
  margin-left: 12px;
  margin-right: 24px;
  justify: flex-end;
  flexGrow: 1;
  ${MQ_FOR_MOBILE_BELOW} {
    margin-left: 0;
  }
`;
